import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { Button } from 'src/components/system/Button';
import { useAuthProfile } from 'src/hooks/useAuthProfile';
import { listSimulations } from 'src/services/simulations';
import { useCourseCategories } from 'src/contexts/CourseCategoriesContext';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 20px;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
`;

const Hero = styled.section`
  position: relative;
  border-radius: 24px;
  padding: 34px;
  background:
    radial-gradient(circle at 84% 18%, rgba(255, 255, 255, 0.24), transparent 38%),
    linear-gradient(125deg, #0f4d4f 0%, #1e4f79 54%, #2f7ca9 100%);
  color: #ffffff;
  overflow: hidden;
  box-shadow: 0 20px 44px rgba(15, 42, 66, 0.2);

  h1 {
    margin: 0;
    font-size: clamp(1.6rem, 3vw, 2.3rem);
    line-height: 1.24;
  }

  p {
    margin: 10px 0 0;
    max-width: 720px;
    color: rgba(255, 255, 255, 0.88);
  }

  @media (max-width: 768px) {
    padding: 24px 20px;
    border-radius: 18px;
  }
`;

const HeroBadges = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const StatsGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const StatCard = styled.article`
  border-radius: 16px;
  padding: 18px;
  border: 1px solid rgba(30, 79, 121, 0.16);
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 10px 24px rgba(15, 42, 66, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;

  strong {
    font-size: 1.7rem;
    color: #10354f;
    line-height: 1;
  }

  span {
    color: #42586a;
    font-size: 0.88rem;
  }
`;

const MainGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.2fr 1fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border-radius: 16px;
  padding: 18px;
  border: 1px solid rgba(30, 79, 121, 0.16);
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 10px 24px rgba(15, 42, 66, 0.08);
  display: flex;
  flex-direction: column;
  gap: 12px;

  h2 {
    margin: 0;
    font-size: 1.1rem;
    color: #163a59;
  }
`;

const MissionList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.92rem;
    color: #31485a;
    line-height: 1.4;
  }
`;

const MissionDot = styled.span<{ done?: boolean }>`
  width: 11px;
  height: 11px;
  margin-top: 3px;
  border-radius: 999px;
  background: ${({ done }) => (done ? '#19a870' : '#1e4f79')};
  box-shadow: 0 0 0 4px ${({ done }) => (done ? 'rgba(25, 168, 112, 0.16)' : 'rgba(30, 79, 121, 0.14)')};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #dbe9f6;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ width: number }>`
  width: ${({ width }) => `${Math.max(0, Math.min(100, width))}%`};
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #1db4a1, #1e4f79);
  transition: width 420ms ease;
`;

const ActionGrid = styled.div`
  display: grid;
  gap: 10px;
`;

type SimulationHistoryItem = {
  id: number;
  status: string;
  correct: number;
  total: number;
  started_at: string;
  finished_at: string | null;
};

function calculateStreak(items: SimulationHistoryItem[]): number {
  const finishedDates = items
    .map(item => item.finished_at)
    .filter((value): value is string => Boolean(value))
    .map(value => new Date(value).toISOString().slice(0, 10));

  const uniqueDays = Array.from(new Set(finishedDates)).sort((left, right) => (left > right ? -1 : 1));
  if (uniqueDays.length === 0) {
    return 0;
  }

  let streak = 1;
  for (let index = 1; index < uniqueDays.length; index += 1) {
    const previous = new Date(uniqueDays[index - 1]);
    const current = new Date(uniqueDays[index]);
    const difference = (previous.getTime() - current.getTime()) / (1000 * 60 * 60 * 24);
    if (difference === 1) {
      streak += 1;
    } else {
      break;
    }
  }

  return streak;
}

export function Home() {
  const navigate = useNavigate();
  const { profile } = useAuthProfile();
  const { categories } = useCourseCategories();
  const [history, setHistory] = useState<SimulationHistoryItem[]>([]);

  useEffect(() => {
    document.title = 'Início | ART - Simulatoro';
  }, []);

  useEffect(() => {
    let active = true;

    listSimulations()
      .then(items => {
        if (!active) return;
        setHistory(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!active) return;
        setHistory([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const finishedSimulations = useMemo(
    () => history.filter(item => item.status === 'finished'),
    [history],
  );

  const averageAccuracy = useMemo(() => {
    if (finishedSimulations.length === 0) {
      return 0;
    }

    let correct = 0;
    let total = 0;
    finishedSimulations.forEach(item => {
      correct += Number(item.correct ?? 0);
      total += Number(item.total ?? 0);
    });

    return total > 0 ? Number(((correct / total) * 100).toFixed(2)) : 0;
  }, [finishedSimulations]);

  const streakDays = useMemo(
    () => calculateStreak(finishedSimulations),
    [finishedSimulations],
  );

  const xp = useMemo(() => {
    const base = finishedSimulations.length * 35;
    const quality = Math.round(averageAccuracy * 2.5);
    return base + quality + streakDays * 25;
  }, [averageAccuracy, finishedSimulations.length, streakDays]);

  const level = useMemo(() => Math.max(1, Math.floor(xp / 240) + 1), [xp]);
  const levelProgress = useMemo(() => ((xp % 240) / 240) * 100, [xp]);

  const nextFamilySlug = categories[0]?.slug ?? 'cfp';
  const ongoingCount = history.filter(item => item.status === 'ongoing').length;

  return (
    <Page>
      <Hero className="stagger-enter">
        <h1>
          Missão ativa{profile?.name ? `, ${profile.name}` : ''}: evoluir performance com simulados inteligentes.
        </h1>
        <p>
          Plataforma educacional com trilha adaptativa, foco em lacunas reais e progresso por níveis.
        </p>
        <HeroBadges>
          <span className="game-badge">Nível {level}</span>
          <span className="game-badge">XP: {xp}</span>
          <span className="game-badge">Sequência: {streakDays} dia(s)</span>
        </HeroBadges>
      </Hero>

      <StatsGrid>
        <StatCard className="stagger-enter">
          <strong>{finishedSimulations.length}</strong>
          <span>Simulados concluídos</span>
        </StatCard>
        <StatCard className="stagger-enter">
          <strong>{averageAccuracy.toFixed(2)}%</strong>
          <span>Acurácia histórica</span>
        </StatCard>
        <StatCard className="stagger-enter">
          <strong>{ongoingCount}</strong>
          <span>Simulados em andamento</span>
        </StatCard>
        <StatCard className="stagger-enter">
          <strong>{Math.round(levelProgress)}%</strong>
          <span>Progresso para o próximo nível</span>
        </StatCard>
      </StatsGrid>

      <MainGrid>
        <Panel className="stagger-enter">
          <h2>Trilha gamificada da semana</h2>
          <ProgressBar>
            <ProgressFill width={levelProgress} />
          </ProgressBar>
          <MissionList>
            <li>
              <MissionDot done={finishedSimulations.length >= 1} />
              Concluir ao menos 1 simulado adaptativo.
            </li>
            <li>
              <MissionDot done={averageAccuracy >= 70} />
              Manter acurácia acima de 70%.
            </li>
            <li>
              <MissionDot done={streakDays >= 3} />
              Atingir sequência mínima de 3 dias de estudo.
            </li>
          </MissionList>
        </Panel>

        <Panel className="stagger-enter">
          <h2>Ações rápidas</h2>
          <ActionGrid>
            <Button
              width="100%"
              backgroundColor="#0f7b6b"
              borderColor="#0f7b6b"
              buttonColor="#ffffff"
              onClick={() => navigate(`/families/${nextFamilySlug}`)}
              label={<span>Iniciar novo simulado</span>}
            />
            <Button
              width="100%"
              backgroundColor="#1e4f79"
              borderColor="#1e4f79"
              buttonColor="#ffffff"
              onClick={() => navigate('/profile?tab=performance')}
              label={<span>Ver recomendação inteligente</span>}
            />
            <Button
              width="100%"
              backgroundColor="#ffffff"
              borderColor="#1e4f79"
              buttonColor="#1e4f79"
              onClick={() => navigate('/quick-game')}
              label={<span>Treino rápido (Desafio)</span>}
            />
          </ActionGrid>
        </Panel>
      </MainGrid>
    </Page>
  );
}

